import React from 'react'
import styled from 'styled-components'
import { stripUnit } from 'polished'
import { bool } from 'prop-types'
import { Link } from 'gatsby'

import { NativeAuthConsumer } from 'components/NativeAuth'
import * as spacing from 'styles/spacing'
import { white, greyDark, greyLight, yellow, black, borderColor } from 'styles/colors'
import { animationTime, animationCurve, navHeight, square } from 'styles/global'
import { fontWeights, fontSizes, Capitalize } from 'styles/typography'
import { mediaDown } from 'styles/media'
import Button from 'components/button/Button'
import Icon from 'components/Icon'

import ProfileCircle from 'icons/profile-circle.svg'

const tierPlans = {
  silver: {
    backgroundColor: greyLight
  },
  gold: {
    backgroundColor: yellow
  },
  platinum: {
    backgroundColor: greyDark
  }
}

const Container = styled.div`
  position: relative;
  flex-shrink: 0;
  margin-left: auto;

  ${mediaDown.medium`
    display: none;
  `};

  &:hover:before {
    content: '';
    display: block;
    position: absolute;
    left: -30px;
    right: -30px;
    top: 0;
    bottom: 0;
    cursor: pointer;
  }
`

const Trigger = styled.span`
  position: relative;
  z-index: 10;
  display: flex;
  align-items: center;
  text-align: left;
  height: ${navHeight};
  transition: color ${animationTime} ${animationCurve};
`

const Dropdown = styled.div`
  background-color: ${white};
  top: 100%;
  left: 50%;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  position: absolute;
  padding: ${spacing.large};
  transition: opacity ${animationTime} ${animationCurve}, visibility ${animationTime} ${animationCurve};
  width: 180px;
  margin-left: -90px;
  z-index: 10;
  overflow: hidden;
  border: 1px solid ${borderColor};

  ${Container}:hover &,
  ${Container}:focus & {
    opacity: 1;
    visibility: visible;
    pointer-events: all;
  }
`

const Item = styled(Link)`
  display: block;
  width: 100%;
  text-align: left;
  transition: color ${animationTime} ${animationCurve};
  font-size: ${fontSizes.small};
  color: ${black};

  & + & {
    margin-top: ${spacing.small};
  }
`

const AvatarWrap = styled.div`
  position: relative;
  margin-right: ${stripUnit(spacing.small) * 1.25 + 'px'};
`

const Avatar = styled.div`
  color: ${props => (props.tier && props.vypePlusMember ? tierPlans[props.tier].backgroundColor : greyLight)};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`

const Plus = styled.div`
  position: absolute;
  bottom: -4px;
  left: -8px;
  border-radius: 50%;
  display: flex;
  text-transform: uppercase;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: ${fontWeights.bold};
  background-color: ${props => (props.transparent ? white : greyDark)};
  color: ${props => (props.transparent ? greyDark : white)};
  box-shadow: ${props => (props.transparent ? 'none' : `0 0 0 2px ${white};`)};
  transition: all ${animationTime} ${animationCurve};
  ${square('16px')};
`

const Account = ({ transparent }) => (
  <Container id="am-exc">
    {/* Dont remove the ID from the container, it's a request from Adviso */}
    <NativeAuthConsumer>
      {({ user, setLoginModalVisibility, logout }) => {
        return user ? (
          <>
            <Link to="/account/" data-hj-suppress>
              <Trigger>
                <AvatarWrap>
                  <Avatar tier={user.currentTierLevel} vypePlusMember={user.vypePlusMember}>
                    <Icon icon={<ProfileCircle />} />
                  </Avatar>
                  {user.currentTierLevel && user.vypePlusMember && <Plus transparent={transparent}>+</Plus>}
                </AvatarWrap>
                <Capitalize>
                  {user.firstName} {user.lastName.charAt(0)}.
                </Capitalize>
              </Trigger>
            </Link>
            <Dropdown>
              <Item to="/account/">My Account</Item>
              <Item to="/account/rewards/">My Rewards</Item>
              <Item to="/account/savings/">My Savings</Item>
              <Item to="/account/orders/">My Orders</Item>
              <Item to="/account/subscriptions/">My Subscription</Item>
              <Item onClick={logout} as="button">
                Logout
              </Item>
            </Dropdown>
          </>
        ) : (
          <Button
            theme="custom"
            variant="outline"
            customColor={transparent ? white : black}
            onClick={() => {
              setLoginModalVisibility(true)
            }}
            data-tracking="nav--link--button--Login/Register"
          >
            Login/Register
          </Button>
        )
      }}
    </NativeAuthConsumer>
  </Container>
)

Account.propTypes = {
  transparent: bool
}

export default Account
